import { useTranslation } from "react-i18next";

import { BackLink } from "@/components/BackLink";
import { FinalCtaSection, H_SECTION, SUBLINE } from "@/components/marketing/Sections";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/lib/seo";
import { BRAND } from "@/config/brand";

/*
 * Shared legal page shell: Terms of Service, Privacy Policy, Cookie Policy.
 * Modular accordion sections — clean hierarchy, no walls of text.
 * i18n structure: legal.{doc}.title / intro / s{1..6}t / s{1..6}b
 */
export default function LegalPage({ doc }) {
  const { t, i18n } = useTranslation();
  const base = `legal.${doc}`;
  usePageMeta({ title: `${t(`${base}.title`)} — ${BRAND.name}`, description: t(`${base}.intro`), localized: true });

  const updated = new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: "long" }).format(new Date(2026, 0, 15));

  return (
    <div data-testid={`legal-${doc}-page`}>
      <section className="bg-white pt-10">
        <div className="max-w-[720px] mx-auto px-6">
          <BackLink fallback="/" testid={`legal-${doc}-back-link`} />
        </div>
        <div className="max-w-[720px] mx-auto px-6 pt-10 lg:pt-14 flex flex-col gap-5">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#1D4D3B]">{t("legal.eyebrow")}</span>
          <h1 className={`${H_SECTION} text-[#1d1d1f]`}>{t(`${base}.title`)}</h1>
          <p className={`${SUBLINE} text-[#6e6e73]`}>{t(`${base}.intro`)}</p>
          <p className="text-xs text-[#86868b]">{t("legal.updated", { date: updated })}</p>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="max-w-[720px] mx-auto px-6">
          <Accordion type="multiple" defaultValue={["s1"]} data-testid={`legal-${doc}-accordion`}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <AccordionItem key={n} value={`s${n}`} className="border-[#d2d2d7]">
                <AccordionTrigger
                  data-testid={`legal-${doc}-trigger-${n}`}
                  className="text-left text-[17px] font-semibold text-[#1d1d1f] py-5 hover:no-underline"
                >
                  <span className="flex items-baseline gap-4">
                    <span className="text-xs font-medium tabular-nums text-[#1D4D3B]">{String(n).padStart(2, "0")}</span>
                    {t(`${base}.s${n}t`)}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#6e6e73] leading-relaxed pb-6 pl-8">
                  {t(`${base}.s${n}b`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-10 text-sm text-[#6e6e73] leading-relaxed bg-[#f5f5f7] rounded-2xl p-6">
            {t("legal.contactNote")}
          </p>
        </div>
      </section>

      <FinalCtaSection />
    </div>
  );
}
